import React, { useRef, useState } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Button, Typography, Box
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import UploadFileTwoToneIcon from '@mui/icons-material/UploadFileTwoTone';
import FileDownloadTwoToneIcon from '@mui/icons-material/FileDownloadTwoTone';
import { CustomerService } from "../../../service/customers.service";

interface Props {
    open: boolean;
    onClose: () => void;
}

// Bulk assign/unassign salespersons to enterprises from a CSV file.
// CSV needs an "EIN" column and a "Salesperson Mobile" (or "Mobile") column; an optional
// "Action" column may say ASSIGN (default) or UNASSIGN per row. Extra columns are ignored,
// so the file downloaded from "Download Enterprises + Salespersons" can be edited and
// re-uploaded as is. Backend: userapi.php action 'bulkAssignEntSalesRep' (max 5000 rows).
const BulkAssignSalesRepDialog = ({ open, onClose }: Props) => {
    const customerService = new CustomerService();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [fileName, setFileName] = useState("");
    // rows parsed from the file that will be sent: {line, ein, mobile, op}
    const [sendRows, setSendRows] = useState<any[]>([]);
    // rows not sent: {line, ein, mobile, reason} (blank salesperson / bad format)
    const [skippedRows, setSkippedRows] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [serverResp, setServerResp] = useState<any>(null);

    const reset = () => {
        setFileName(""); setSendRows([]); setSkippedRows([]); setServerResp(null); setUploading(false);
        if (fileInputRef.current) { fileInputRef.current.value = ""; }
    };
    const handleClose = () => { if (!uploading) { reset(); onClose(); } };

    const errToast = (msg: string) => toast.error(msg, { position: toast.POSITION.BOTTOM_CENTER, autoClose: 3500, hideProgressBar: true });

    // minimal RFC-4180-ish parser (quoted cells, embedded commas/quotes/newlines)
    const parseCsv = (text: string): string[][] => {
        const rows: string[][] = [];
        let cur: string[] = [], field = "", inQuotes = false;
        for (let i = 0; i < text.length; i++) {
            const c = text[i];
            if (inQuotes) {
                if (c === '"') {
                    if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
                } else { field += c; }
            } else if (c === '"') { inQuotes = true; }
            else if (c === ',') { cur.push(field); field = ""; }
            else if (c === '\n') { cur.push(field); rows.push(cur); cur = []; field = ""; }
            else if (c === '\r') { /* swallow */ }
            else { field += c; }
        }
        if (field !== "" || cur.length > 0) { cur.push(field); rows.push(cur); }
        return rows;
    };

    const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
        setServerResp(null); setSendRows([]); setSkippedRows([]);
        const file = e.target.files && e.target.files[0];
        if (!file) { return; }
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result || "").replace(/^\uFEFF/, "");
            const grid = parseCsv(text).filter(r => r.some(c => c.trim() !== ""));
            if (grid.length < 2) { errToast("File has no data rows"); return; }
            const header = grid[0].map(h => h.trim().toLowerCase());
            const einCol = header.indexOf("ein");
            let mobCol = header.indexOf("salesperson mobile");
            if (mobCol < 0) { mobCol = header.indexOf("mobile"); }
            if (mobCol < 0) { mobCol = header.indexOf("mobile no"); }
            if (mobCol < 0) { mobCol = header.indexOf("mobileno"); }
            const actCol = header.indexOf("action");
            if (einCol < 0 || mobCol < 0) {
                errToast('Header must contain "EIN" and "Salesperson Mobile" (or "Mobile") columns');
                return;
            }
            const toSend: any[] = []; const skipped: any[] = [];
            for (let i = 1; i < grid.length; i++) {
                const line = i + 1; // 1-based incl. header, matches what Excel shows
                const ein = (grid[i][einCol] || "").trim().toUpperCase();
                const mobileRaw = (grid[i][mobCol] || "").trim();
                const mobile = mobileRaw.replace(/[^0-9]/g, "").slice(-10);
                const actRaw = actCol >= 0 ? (grid[i][actCol] || "").trim().toUpperCase() : "";
                const op = actRaw === "UNASSIGN" || actRaw === "U" ? "U" : "A";
                if (ein === "" && mobileRaw === "") { continue; }
                if (mobileRaw === "" || mobileRaw.toUpperCase() === "UNASSIGNED") {
                    skipped.push({ line, ein, mobile: mobileRaw, reason: "no salesperson on row" });
                    continue;
                }
                if (!/^[A-Z0-9/\-_.]{1,64}$/.test(ein) || mobile.length !== 10) {
                    skipped.push({ line, ein, mobile: mobileRaw, reason: "bad EIN or mobile format" });
                    continue;
                }
                toSend.push({ line, ein, mobile, op });
            }
            if (toSend.length === 0) { errToast("No usable rows found in the file"); }
            if (toSend.length > 5000) { errToast("Too many rows (max 5000)"); return; }
            setSendRows(toSend); setSkippedRows(skipped);
        };
        reader.readAsText(file);
    };

    const upload = () => {
        if (sendRows.length === 0) { return; }
        setUploading(true);
        customerService.bulkAssignEntSalesRep(sendRows.map(r => ({ ein: r.ein, mobile: r.mobile, op: r.op }))).then((res: any) => {
            setUploading(false);
            if (res != null && res.status == 100 && res.data && Array.isArray(res.data.results)) {
                setServerResp(res.data);
            } else {
                errToast((res && res.msg) ? res.msg : "Upload failed !!!");
            }
        }).catch(() => {
            setUploading(false);
            errToast("Network error during upload");
        });
    };

    const csvEscape = (v: any) => '"' + String(v === null || v === undefined ? "" : v).replace(/"/g, '""') + '"';
    const saveCsv = (name: string, lines: string[]) => {
        const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const downloadTemplate = () => {
        saveCsv("bulk_assign_template.csv", [
            ["EIN", "Salesperson Mobile", "Action"].map(csvEscape).join(","),
            ["N02229", "9876543210", "ASSIGN"].map(csvEscape).join(","),
            ["N02229", "9123456780", "UNASSIGN"].map(csvEscape).join(","),
        ]);
    };

    const downloadResults = () => {
        if (!serverResp) { return; }
        const lines = [["File Line", "EIN", "Mobile", "Action", "Result"].map(csvEscape).join(",")];
        serverResp.results.forEach((r: any, i: number) => {
            const line = sendRows[i] ? sendRows[i].line : "";
            lines.push([line, r.ein, r.mobile, r.op === "U" ? "UNASSIGN" : "ASSIGN", r.result].map(csvEscape).join(","));
        });
        skippedRows.forEach((s: any) => {
            lines.push([s.line, s.ein, s.mobile, "", "skipped: " + s.reason].map(csvEscape).join(","));
        });
        saveCsv("bulk_assign_results_" + new Date().toISOString().slice(0, 10) + ".csv", lines);
    };

    const failures = serverResp ? serverResp.results.filter((r: any) =>
        r.result !== "assigned" && r.result !== "unassigned" && r.result !== "already_assigned" && r.result !== "already_unassigned") : [];
    const assignCnt = sendRows.filter(r => r.op === "A").length;
    const unassignCnt = sendRows.length - assignCnt;

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose} aria-labelledby="bulk-assign-title">
            <DialogTitle sx={{ m: 0, p: 2 }} id="bulk-assign-title">
                Bulk Assign Salespersons (CSV)
                <IconButton aria-label="close" onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Upload a CSV with columns <b>EIN</b> and <b>Salesperson Mobile</b> (one row per
                    enterprise–salesperson pair; the same EIN may appear on several rows). An optional
                    <b> Action</b> column may say ASSIGN (default) or UNASSIGN. Extra columns are ignored —
                    you can edit the file from "Download Enterprises + Salespersons" and upload it back.
                    Existing assignments not mentioned in the file are left untouched.
                </Typography>
                <Button size="small" onClick={downloadTemplate}>Download template</Button>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Button variant="outlined" component="label" startIcon={<UploadFileTwoToneIcon />} disabled={uploading}>
                        Choose CSV
                        <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={onFilePicked} />
                    </Button>
                    <Typography variant="body2">{fileName || "no file selected"}</Typography>
                </Box>

                {sendRows.length > 0 && !serverResp && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Ready to apply: <b>{assignCnt}</b> assign{unassignCnt > 0 && (<span>, <b>{unassignCnt}</b> unassign</span>)}
                        {skippedRows.length > 0 && (<span> — {skippedRows.length} row(s) skipped (no salesperson / bad format)</span>)}
                    </Typography>
                )}

                {serverResp && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Applied: <b style={{ color: 'green' }}>{serverResp.applied}</b> &nbsp;|&nbsp;
                            Already as requested: <b>{serverResp.already}</b> &nbsp;|&nbsp;
                            Failed: <b style={{ color: failures.length > 0 ? 'red' : 'inherit' }}>{serverResp.failed}</b>
                            {skippedRows.length > 0 && (<span> &nbsp;|&nbsp; Skipped before upload: <b>{skippedRows.length}</b></span>)}
                        </Typography>
                        {failures.slice(0, 15).map((r: any, i: number) => (
                            <Typography key={i} variant="body2" sx={{ color: 'error.main', fontSize: 12 }}>
                                {r.ein} / {r.mobile} → {r.result}
                            </Typography>
                        ))}
                        {failures.length > 15 && (
                            <Typography variant="body2" sx={{ fontSize: 12 }}>… {failures.length - 15} more, see results file</Typography>
                        )}
                        <Button size="small" startIcon={<FileDownloadTwoToneIcon />} onClick={downloadResults} sx={{ mt: 1 }}>
                            Download results CSV
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>{serverResp ? "Close" : "Cancel"}</Button>
                {!serverResp && (
                    <Button onClick={upload} variant="contained" disabled={uploading || sendRows.length === 0}>
                        {uploading ? "Uploading..." : "Apply " + (sendRows.length > 0 ? "(" + sendRows.length + " rows)" : "")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkAssignSalesRepDialog;
